import { createContext, useContext, type ReactNode } from 'react';

import type { ResolvedUiCommand, UiCommand } from './types';

/*
 * ОДИН ДОГОВОР КОМАНД НА ВЕСЬ ПРОДУКТ — И ДВА ИСТОЧНИКА ПОД НИМ.
 *
 * Замер ДО (2026-09-13, этот ворктри): палитра команд, экран сокращений и
 * общее контекстное меню жили ТОЛЬКО во вьюере — в Mesh Studio их было ноль
 * (`grep -rln 'features/command-palette' features/mesh-studio` → пусто).
 * При этом у самой Mesh Studio УЖЕ был свой работающий реестр: 59 команд в
 * шести группах (`pro100-command-catalog.ts`), своя строка меню и свой
 * исполнитель `runPro100Command`.
 *
 * Отсюда два неверных хода, которых здесь нет:
 *   · принести во вьюер-палитру команды PRO100 «вторым списком» — это второй
 *     реестр на один продукт, ровно то, что сегодня сводили у контекстных меню;
 *   · обернуть Mesh Studio в `WorkbenchRuntimeProvider` — он завязан на
 *     `viewer-store`, `ui-store`, мутации undo/redo и «Кухню по изображениям»,
 *     то есть притащил бы весь вьюер в студию ради одного окна поиска.
 *
 * Верный ход — разделить ДОГОВОР и ИСТОЧНИК. Договор (`commands`, `resolve`,
 * `execute`) объявлен здесь и больше нигде; палитра и экран сокращений читают
 * ровно его. Источников два, и каждый остаётся хозяином своих команд: вьюер
 * отдаёт `WORKBENCH_COMMANDS`, Mesh Studio — проекцию своего каталога PRO100.
 * Поверхность при этом ОДНА: файл палитры один, файл экрана сокращений один.
 */

/**
 * Жест — клавиша или движение мыши, у которых по определению нет пункта меню.
 * Их список ТОЖЕ принадлежит владельцу: `Esc` во вьюере поднимается по оси
 * «деталь → модуль → композиция → комната», а в Mesh Studio ни композиций, ни
 * комнат нет, и та же строка была бы обещанием того, чего нет.
 */
export type WorkbenchGesture = Readonly<{
  keys: string;
  label: string;
  hint: string;
  commandId: string;
}>;

export type WorkbenchContextValue = {
  commands: readonly UiCommand[];
  resolve: (command: UiCommand) => ResolvedUiCommand;
  execute: (commandId: string) => void | Promise<void>;
  /** Жесты этого рабочего места. Не задан — берутся жесты вьюера. */
  gestures?: readonly WorkbenchGesture[];
};

const WorkbenchContext = createContext<WorkbenchContextValue | null>(null);

/**
 * Низкоуровневый владелец договора: принимает готовое значение.
 *
 * `WorkbenchRuntimeProvider` вьюера строит своё значение и отдаёт сюда;
 * Mesh Studio строит своё. Ни один из них не знает про другой.
 */
export function WorkbenchCommandProvider({
  value,
  children,
}: {
  value: WorkbenchContextValue;
  children: ReactNode;
}) {
  return <WorkbenchContext.Provider value={value}>{children}</WorkbenchContext.Provider>;
}

/**
 * Договор, ЕСЛИ он смонтирован. Для поверхностей, которые обязаны работать и
 * вне модуля — экран сокращений открывается и в голом рендере пина.
 * Возвращает `null` вместо броска: отсутствие владельца здесь законно и
 * названо, а не проглочено внутри `try`.
 */
export function useWorkbenchCommandsOptional(): WorkbenchContextValue | null {
  return useContext(WorkbenchContext);
}

export function useWorkbenchRuntime(): WorkbenchContextValue {
  const value = useContext(WorkbenchContext);
  if (!value) throw new Error('useWorkbenchRuntime must be used inside WorkbenchRuntimeProvider');
  return value;
}
